import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../../src/lib/supabase';
import { logUsageEvent } from '../../src/lib/logger';
import { Fatura } from '../../types';
import {
    Loader2, Search, FileText, Download, Check, Copy, Upload, AlertCircle,
    CheckCircle2, AlertTriangle, CalendarDays, TrendingUp, DollarSign, ExternalLink
} from 'lucide-react';
import { useToast } from '../../src/contexts/ToastContext';
import { useAuth } from '../../src/contexts/AuthContext';
import { formatDate } from '../../src/lib/utils';
import { ucCodigoKeys } from '../../src/lib/ucCodigo';
import ConfirmModal from '../../src/components/ConfirmModal';
import { hojeISO, montarConfirmacaoPagamentoManual, lerValorPago, valorDoBoleto, formatarValorPago } from '../../src/lib/confirmacaoPagamentoManual';
import { derivarStatusBoleto } from '../../src/lib/statusBoleto';

interface HistoricoCobrancasTabProps {
    unidadeId: string;
    numeroUc: string;
    outroNumeroUc?: string;
}

const STATUS_BOLETO_CONFIG: Record<string, { bg: string; text: string; border: string; label: string }> = {
    'pago': {
        bg: 'bg-emerald-50 dark:bg-emerald-950/30',
        text: 'text-emerald-700 dark:text-emerald-400',
        border: 'border-emerald-200 dark:border-emerald-900/50',
        label: 'Pago'
    },
    'em aberto': {
        bg: 'bg-amber-50 dark:bg-amber-950/30',
        text: 'text-amber-700 dark:text-amber-400',
        border: 'border-amber-200 dark:border-amber-900/50',
        label: 'Em aberto'
    },
    'vencido': {
        bg: 'bg-red-50 dark:bg-red-950/30',
        text: 'text-red-700 dark:text-red-400',
        border: 'border-red-200 dark:border-red-900/50',
        label: 'Vencido'
    },
    'cancelado': {
        bg: 'bg-gray-100 dark:bg-gray-800/50',
        text: 'text-gray-600 dark:text-gray-400',
        border: 'border-gray-200 dark:border-gray-700',
        label: 'Cancelado'
    },
    'parcelado': {
        bg: 'bg-indigo-50 dark:bg-indigo-950/30',
        text: 'text-indigo-700 dark:text-indigo-400',
        border: 'border-indigo-200 dark:border-indigo-900/50',
        label: 'Parcelado'
    },
    'quitado': {
        bg: 'bg-teal-50 dark:bg-teal-950/30',
        text: 'text-teal-700 dark:text-teal-400',
        border: 'border-teal-200 dark:border-teal-900/50',
        label: 'Quitado'
    },
    // Ciclo sem compensação SCEE: existe a fatura da distribuidora, não existe
    // boleto da Vigor. Não é dívida — ver o comentário do bloco de status abaixo.
    'sem injeção': {
        bg: 'bg-gray-100 dark:bg-gray-800/30',
        text: 'text-gray-500 dark:text-gray-400',
        border: 'border-gray-200 dark:border-gray-700/50',
        label: 'Sem injeção'
    },
    'sem cobrança': {
        bg: 'bg-gray-100 dark:bg-gray-800/30',
        text: 'text-gray-500 dark:text-gray-400',
        border: 'border-gray-200 dark:border-gray-700/50',
        label: 'Sem cobrança'
    },
    // UC de agrupamento (Prefeitura): quem cobra é o boleto agrupado da Secretaria (#6284).
    'cobrança agrupada': {
        bg: 'bg-violet-50 dark:bg-violet-950/30',
        text: 'text-violet-700 dark:text-violet-400',
        border: 'border-violet-200 dark:border-violet-900/50',
        label: 'Cobrança agrupada'
    }
};

/** Status derivados em que existe boleto vivo para pagar/cobrar. */
const STATUS_COBRANCA_VIVA = ['em aberto', 'vencido'];

const HistoricoCobrancasTab: React.FC<HistoricoCobrancasTabProps> = ({ unidadeId, numeroUc, outroNumeroUc }) => {
    const [loading, setLoading] = useState(true);
    const [faturas, setFaturas] = useState<Fatura[]>([]);
    const backdropMouseDownRef = useRef(false);

    // Search & Filter State
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState('todos');

    // Clipboard copy state mapping to show "checked" feedback per invoice/type
    const [copyState, setCopyState] = useState<Record<string, boolean>>({});

    // Confirmation & Uploading State
    const [confirmingPaidFatura, setConfirmingPaidFatura] = useState<Fatura | null>(null);
    const [receiptFile, setReceiptFile] = useState<File | null>(null);
    const [receiptDate, setReceiptDate] = useState(hojeISO());
    const [receiptValor, setReceiptValor] = useState('');
    const receiptValorLido = lerValorPago(receiptValor);
    const [paymentLoading, setPaymentLoading] = useState(false);

    const { addToast } = useToast();
    const { user } = useAuth();

    // Fetch data
    const fetchFaturas = async () => {
        setLoading(true);
        try {
            const ucKeys = ucCodigoKeys(numeroUc, outroNumeroUc);
            if (ucKeys.length === 0) {
                setFaturas([]);
                return;
            }

            const { data, error } = await supabase
                .from('faturas')
                .select('*')
                .in('uc_codigo_norm', ucKeys)
                .neq('status_fatura', 'Excluído');

            if (error) throw error;
            setFaturas(data || []);
        } catch (err) {
            console.error("Erro ao buscar histórico de cobranças:", err);
            addToast("Erro ao carregar o histórico.", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (numeroUc) {
            fetchFaturas();
        }
    }, [numeroUc, outroNumeroUc]);

    // Parsing reference month "MM/YYYY" or "MES/YYYY" for precise sorting
    const parseMesReferencia = (ref: string) => {
        if (!ref) return 0;
        const clean = ref.trim().toUpperCase().replace('-', '/');
        const parts = clean.split('/');
        if (parts.length !== 2) return 0;

        let month = parseInt(parts[0], 10);
        if (isNaN(month)) {
            const monthMap: Record<string, number> = {
                'JAN': 1, 'FEV': 2, 'MAR': 3, 'ABR': 4, 'MAI': 5, 'JUN': 6,
                'JUL': 7, 'AGO': 8, 'SET': 9, 'OUT': 10, 'NOV': 11, 'DEZ': 12
            };
            month = monthMap[parts[0].substring(0, 3)] || 0;
        }
        const year = parseInt(parts[1], 10);
        if (isNaN(year) || isNaN(month)) return 0;
        return year * 100 + month;
    };

    // Status REAL de cada fatura, pela fonte única de `src/lib/statusBoleto`.
    //
    // Antes esta aba lia `status_boleto_vigor` cru e tratava campo vazio como
    // "Em aberto" (`fatura.status_boleto_vigor || 'em aberto'`), com um único
    // escape: valor `null` virava "Não se aplica". Isso deixava de fora
    // exatamente as faturas de "pular cálculo", que gravam valor **0** — elas
    // apareciam como cobrança da Vigor em aberto/vencida de R$ 0,00, sem boleto
    // para baixar e sem ação de quitação (chamado da Dhaiane, 04/09/2026, UC da
    // FABRIK LTDA; 967 faturas na mesma situação na base).
    //
    // `derivarStatusBoleto` já resolve os dois casos: sem valor, devolve
    // 'Sem injeção' (a UC não recebeu compensação no ciclo) ou 'Sem cobrança'.
    const statusPorFatura = useMemo(() => {
        const agora = new Date();
        return new Map<string, string>(
            faturas.map(f => [f.id, derivarStatusBoleto(f as any, agora).toLowerCase()])
        );
    }, [faturas]);

    const statusDe = (f: Fatura) => statusPorFatura.get(f.id) || 'sem cobrança';

    // Sorted and Filtered list
    const processedFaturas = useMemo(() => {
        let list = [...faturas];

        // Search text filter
        if (searchText.trim() !== '') {
            const search = searchText.toLowerCase();
            list = list.filter(f =>
                (f.mes_referencia || '').toLowerCase().includes(search) ||
                (f.id || '').toString().includes(search)
            );
        }

        // Status filter
        if (statusFilter !== 'todos') {
            list = list.filter(f => statusPorFatura.get(f.id) === statusFilter);
        }

        // Sort chronologically decrescent by MM/YYYY
        list.sort((a, b) => {
            const dateA = parseMesReferencia(a.mes_referencia || '');
            const dateB = parseMesReferencia(b.mes_referencia || '');
            return dateB - dateA;
        });

        return list;
    }, [faturas, searchText, statusFilter, statusPorFatura]);

    // Tab KPIs
    const kpis = useMemo(() => {
        const activeFaturas = faturas.filter(f => statusPorFatura.get(f.id) !== 'cancelado');
        const somaPorStatus = (status: string) => activeFaturas
            .filter(f => statusPorFatura.get(f.id) === status)
            .reduce((acc, f) => acc + (Number(f.valor_boleto_vigor) || 0), 0);

        const totalFaturado = activeFaturas.reduce((acc, f) => acc + (Number(f.valor_boleto_vigor) || 0), 0);
        const totalPago = somaPorStatus('pago');
        const totalEmAberto = somaPorStatus('em aberto');
        const totalVencido = somaPorStatus('vencido');

        const totalEconomia = faturas.reduce((acc, f) => acc + (Number(f.economia) || 0), 0);

        return {
            totalFaturado,
            totalPago,
            totalEmAberto,
            totalVencido,
            totalEconomia,
            count: faturas.length
        };
    }, [faturas, statusPorFatura]);

    // Handle copying PIX / Linha Digitável
    const handleCopy = (text: string, id: string, type: 'pix' | 'linha') => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        const key = `${id}-${type}`;
        setCopyState(prev => ({ ...prev, [key]: true }));
        addToast(`${type === 'pix' ? 'Código PIX' : 'Linha Digitável'} copiado!`, 'success');
        setTimeout(() => {
            setCopyState(prev => ({ ...prev, [key]: false }));
        }, 2000);
    };

    // Confirms payment and uploads receipt
    const handleConfirmPayment = async () => {
        if (!confirmingPaidFatura) return;
        if (!receiptFile) {
            addToast('O envio do comprovante é obrigatório.', 'error');
            return;
        }
        if (!receiptDate) {
            addToast('Informe a data em que o cliente pagou.', 'error');
            return;
        }
        if (receiptDate > hojeISO()) {
            addToast('A data do pagamento não pode ser no futuro.', 'error');
            return;
        }
        if (receiptValorLido == null) {
            addToast('Informe o valor pago, como está no comprovante.', 'error');
            return;
        }

        setPaymentLoading(true);
        try {
            const fileExt = receiptFile.name.split('.').pop();
            const fileName = `receipt-${confirmingPaidFatura.id}-${Date.now()}.${fileExt}`;
            const { error: uploadError } = await supabase.storage
                .from('comprovantes')
                .upload(fileName, receiptFile);

            if (uploadError) {
                throw new Error('Falha ao enviar o comprovante: ' + uploadError.message);
            }

            const { data: publicUrlData } = supabase.storage
                .from('comprovantes')
                .getPublicUrl(fileName);

            const publicUrl = publicUrlData.publicUrl;

            // Grava a data informada além do status — sem ela o boleto fica "pago
            // sem data" e vira falso vencido nos painéis (chamado #3789). A guarda
            // do boleto único vive em src/lib/confirmacaoPagamentoManual.ts.
            const { camposUpdate, ucBoletoUnico } = await montarConfirmacaoPagamentoManual(
                confirmingPaidFatura.uc_codigo,
                publicUrl,
                receiptDate,
                receiptValorLido
            );

            const { error: updateError } = await supabase
                .from('faturas')
                .update(camposUpdate)
                .eq('id', confirmingPaidFatura.id);

            if (updateError) throw updateError;
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'faturas', acao: 'confirmar_pagamento_manual', origem: 'acao' });

            addToast('Pagamento confirmado com sucesso!', 'success');
            if (ucBoletoUnico) {
                addToast('UC de boleto único: repasse à Equatorial segue pendente de ação manual.', 'info');
            }
            setConfirmingPaidFatura(null);
            setReceiptFile(null);
            setReceiptDate(hojeISO());
            setReceiptValor('');

            // Refresh local list
            fetchFaturas();
        } catch (error: any) {
            console.error('Erro ao registrar pagamento:', error);
            addToast(error.message || 'Erro ao registrar pagamento.', 'error');
        } finally {
            setPaymentLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center p-16 text-gray-400">
                <Loader2 className="animate-spin mb-3 text-primary" size={36} />
                <p className="text-sm font-semibold">Buscando histórico de cobranças...</p>
            </div>
        );
    }

    return (
        <div className="p-6 space-y-6 animate-fade-in text-gray-900 dark:text-gray-100">

            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">

                {/* Economia */}
                <div className="bg-emerald-50/50 dark:bg-emerald-950/10 border border-emerald-150 dark:border-emerald-900/30 rounded-xl p-4 shadow-sm flex items-center gap-4">
                    <div className="p-3 bg-emerald-100 dark:bg-emerald-900/40 rounded-lg text-emerald-600 dark:text-emerald-400">
                        <TrendingUp size={24} />
                    </div>
                    <div>
                        <p className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Economia Total</p>
                        <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400 mt-0.5">
                            R$ {kpis.totalEconomia.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                    </div>
                </div>

                {/* Total Faturado */}
                <div className="bg-white dark:bg-gray-850 border border-gray-200 dark:border-gray-800 rounded-xl p-4 shadow-sm flex items-center gap-4">
                    <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-lg text-gray-500">
                        <DollarSign size={24} />
                    </div>
                    <div>
                        <p className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total Emitido</p>
                        <p className="text-xl font-bold text-gray-900 dark:text-white mt-0.5">
                            R$ {kpis.totalFaturado.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                    </div>
                </div>

                {/* Total Pago */}
                <div className="bg-teal-50/30 dark:bg-teal-950/10 border border-teal-150 dark:border-teal-900/20 rounded-xl p-4 shadow-sm flex items-center gap-4">
                    <div className="p-3 bg-teal-100 dark:bg-teal-950/30 rounded-lg text-teal-600 dark:text-teal-400">
                        <CheckCircle2 size={24} />
                    </div>
                    <div>
                        <p className="text-[10px] font-bold text-teal-600 dark:text-teal-400 uppercase tracking-wider">Total Pago</p>
                        <p className="text-xl font-bold text-teal-600 dark:text-teal-400 mt-0.5">
                            R$ {kpis.totalPago.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                    </div>
                </div>

                {/* Total em Aberto */}
                <div className="bg-amber-50/50 dark:bg-amber-950/10 border border-amber-150 dark:border-amber-900/30 rounded-xl p-4 shadow-sm flex items-center gap-4">
                    <div className="p-3 bg-amber-100 dark:bg-amber-900/40 rounded-lg text-amber-600 dark:text-amber-400">
                        <AlertCircle size={24} />
                    </div>
                    <div>
                        <p className="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wider">Em aberto</p>
                        <p className="text-xl font-bold text-amber-600 dark:text-amber-400 mt-0.5">
                            R$ {kpis.totalEmAberto.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                    </div>
                </div>

                {/* Total Vencido */}
                <div className="bg-red-50/50 dark:bg-red-950/10 border border-red-150 dark:border-red-900/30 rounded-xl p-4 shadow-sm flex items-center gap-4">
                    <div className="p-3 bg-red-100 dark:bg-red-900/40 rounded-lg text-red-600 dark:text-red-400">
                        <AlertTriangle size={24} />
                    </div>
                    <div>
                        <p className="text-[10px] font-bold text-red-600 dark:text-red-400 uppercase tracking-wider">Vencidos</p>
                        <p className="text-xl font-bold text-red-600 dark:text-red-400 mt-0.5">
                            R$ {kpis.totalVencido.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                    </div>
                </div>

            </div>

            {/* Filter and Search Bar */}
            <div className="flex flex-col sm:flex-row gap-4 bg-gray-50 dark:bg-gray-900/40 p-4 rounded-xl border border-gray-200 dark:border-gray-800">

                {/* Search */}
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                        type="text"
                        placeholder="Buscar por mês de referência (ex: 05/2026)..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-850 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    />
                </div>

                {/* Filter */}
                <div className="flex items-center gap-2">
                    <label className="text-xs font-bold text-gray-500 uppercase whitespace-nowrap">Status Boleto:</label>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-850 text-gray-900 dark:text-white outline-none cursor-pointer focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    >
                        <option value="todos">Todos os Status</option>
                        <option value="em aberto">Em aberto</option>
                        <option value="pago">Pago</option>
                        <option value="vencido">Vencido</option>
                        <option value="quitado">Quitado</option>
                        <option value="parcelado">Parcelado</option>
                        <option value="cancelado">Cancelado</option>
                        <option value="sem injeção">Sem injeção</option>
                        <option value="sem cobrança">Sem cobrança</option>
                    </select>
                </div>

            </div>

            {/* Tabela de Cobranças */}
            <div className="bg-white dark:bg-gray-850 rounded-2xl border border-gray-200 dark:border-gray-850 overflow-hidden shadow-sm">

                {processedFaturas.length > 0 ? (
                    <div className="overflow-x-auto scroller">
                        <table className="w-full text-left text-sm border-collapse">
                            <thead>
                                <tr className="bg-gray-50 dark:bg-gray-900 border-b border-gray-100 dark:border-gray-850 text-gray-500 dark:text-gray-400 text-[10px] font-bold uppercase tracking-wider">
                                    <th className="px-6 py-4">Ref.</th>
                                    <th className="px-6 py-4">Distribuidora</th>
                                    <th className="px-6 py-4">Economia</th>
                                    <th className="px-6 py-4">Boleto Vigor</th>
                                    <th className="px-6 py-4">Vencimento</th>
                                    <th className="px-6 py-4">Status</th>
                                    <th className="px-6 py-4 text-center">Ações</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                                {processedFaturas.map((fatura) => {
                                    const statusLower = statusDe(fatura);
                                    const temCobrancaViva = STATUS_COBRANCA_VIVA.includes(statusLower);
                                    const statusCfg = STATUS_BOLETO_CONFIG[statusLower] || STATUS_BOLETO_CONFIG['sem cobrança'];

                                    return (
                                        <tr key={fatura.id} className="hover:bg-gray-50/50 dark:hover:bg-gray-800/20 transition-colors">

                                            {/* Mês de Referência */}
                                            <td className="px-6 py-4 font-bold text-gray-900 dark:text-white">
                                                {fatura.mes_referencia}
                                                {fatura.dono_anterior_troca_id && (
                                                    <span
                                                        className="ml-2 inline-block text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 align-middle"
                                                        title="Fatura do dono anterior da UC (troca de dono): continua no nome dele e não é cobrada de quem comprou."
                                                    >
                                                        Dono anterior
                                                    </span>
                                                )}
                                            </td>

                                            {/* Valor Distribuidora */}
                                            <td className="px-6 py-4">
                                                <p className="font-semibold text-gray-700 dark:text-gray-300">
                                                    R$ {Number(fatura.valor_fatura_distribuidora || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </p>
                                                {fatura.pdf_fatura ? (
                                                    <a
                                                        href={fatura.pdf_fatura}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-[10px] text-primary hover:text-primary-dark hover:underline flex items-center gap-1 mt-1 font-bold"
                                                    >
                                                        <FileText size={10} /> Fatura Concessionária
                                                    </a>
                                                ) : (
                                                    <span className="text-[10px] text-gray-400 mt-1 block">Fatura indisponível</span>
                                                )}
                                            </td>

                                            {/* Economia */}
                                            <td className="px-6 py-4 text-emerald-600 dark:text-emerald-400 font-bold">
                                                R$ {Number(fatura.economia || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </td>

                                            {/* Valor Boleto Vigor */}
                                            <td className="px-6 py-4">
                                                {fatura.valor_boleto_vigor !== null && fatura.valor_boleto_vigor !== undefined ? (
                                                    <div>
                                                        <p className="font-extrabold text-primary">
                                                            R$ {Number(fatura.valor_boleto_vigor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                        </p>
                                                        {fatura.url_boleto ? (
                                                            <a
                                                                href={fatura.url_boleto}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-[10px] text-primary hover:text-primary-dark hover:underline flex items-center gap-1 mt-1 font-bold"
                                                            >
                                                                <Download size={10} /> Baixar Boleto
                                                            </a>
                                                        ) : (
                                                            <span className="text-[10px] text-gray-400 mt-1 block">Boleto não gerado</span>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="text-gray-400 text-xs italic">
                                                        {fatura.motivo_pular_calculo || 'Não calculado'}
                                                    </span>
                                                )}
                                            </td>

                                            {/* Vencimento */}
                                            <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300 font-medium">
                                                {fatura.vencimento_boleto
                                                    ? formatDate(fatura.vencimento_boleto)
                                                    : fatura.data_vencimento
                                                        ? formatDate(fatura.data_vencimento)
                                                        : '-'}
                                            </td>

                                            {/* Status Badge */}
                                            <td className="px-6 py-4">
                                                <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-extrabold border ${statusCfg.bg} ${statusCfg.text} ${statusCfg.border}`}>
                                                    {statusCfg.label}
                                                </span>
                                            </td>

                                            {/* Ações */}
                                            <td className="px-6 py-4">
                                                <div className="flex flex-col gap-2 max-w-[240px] mx-auto text-xs">

                                                    {/* Copy PIX */}
                                                    {fatura.codigo_pix_boleto && temCobrancaViva && (
                                                        <button
                                                            onClick={() => handleCopy(fatura.codigo_pix_boleto!, fatura.id.toString(), 'pix')}
                                                            className="flex items-center justify-between px-3 py-1.5 bg-gray-50 dark:bg-gray-800 hover:bg-primary/5 hover:text-primary dark:hover:text-primary dark:hover:bg-primary/10 rounded-lg font-bold border border-gray-200 dark:border-gray-700 transition-all"
                                                        >
                                                            <span className="flex items-center gap-1">Copiar PIX</span>
                                                            {copyState[`${fatura.id}-pix`] ? (
                                                                <Check className="text-emerald-500" size={14} />
                                                            ) : (
                                                                <Copy size={12} className="text-gray-400" />
                                                            )}
                                                        </button>
                                                    )}

                                                    {/* Copy Linha Digitável */}
                                                    {fatura.linha_digitavel_boleto && temCobrancaViva && (
                                                        <button
                                                            onClick={() => handleCopy(fatura.linha_digitavel_boleto!, fatura.id.toString(), 'linha')}
                                                            className="flex items-center justify-between px-3 py-1.5 bg-gray-50 dark:bg-gray-800 hover:bg-primary/5 hover:text-primary dark:hover:text-primary dark:hover:bg-primary/10 rounded-lg font-bold border border-gray-200 dark:border-gray-700 transition-all"
                                                        >
                                                            <span>Copiar Código</span>
                                                            {copyState[`${fatura.id}-linha`] ? (
                                                                <Check className="text-emerald-500" size={14} />
                                                            ) : (
                                                                <Copy size={12} className="text-gray-400" />
                                                            )}
                                                        </button>
                                                    )}

                                                    {/* Comprovante link */}
                                                    {fatura.comprovante_pagamento_url ? (
                                                        <a
                                                            href={fatura.comprovante_pagamento_url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="flex items-center justify-center gap-1.5 px-3 py-1.5 bg-teal-50 dark:bg-teal-950/20 text-teal-700 dark:text-teal-400 hover:bg-teal-100 dark:hover:bg-teal-950/30 rounded-lg font-bold border border-teal-200 dark:border-teal-900/50 transition-colors"
                                                        >
                                                            <ExternalLink size={12} /> Ver Comprovante
                                                        </a>
                                                    ) : (
                                                        temCobrancaViva && (
                                                            <button
                                                                onClick={() => {
                                                                    setReceiptValor(formatarValorPago(valorDoBoleto(fatura)));
                                                                    setConfirmingPaidFatura(fatura);
                                                                }}
                                                                className="flex items-center justify-center gap-1.5 px-3 py-1.5 bg-primary/10 hover:bg-primary/20 text-primary rounded-lg font-bold transition-colors border border-transparent"
                                                            >
                                                                <Upload size={12} /> Confirmar Pago
                                                            </button>
                                                        )
                                                    )}

                                                </div>
                                            </td>

                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="text-center py-16 text-gray-500 dark:text-gray-400 bg-gray-50/50 dark:bg-gray-900/10">
                        <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-450">
                            <CalendarDays size={28} />
                        </div>
                        <p className="text-base font-bold text-gray-800 dark:text-white">Nenhum registro encontrado</p>
                        <p className="text-sm mt-1 max-w-sm mx-auto">
                            Não encontramos faturas ou cobranças ativas para esta unidade consumidora no momento.
                        </p>
                    </div>
                )}

            </div>

            {/* Confirm Paid modal. No body: dentro do modal da UC (que tem transform
                na animação) o `fixed` ficava preso à área da aba e cortava os botões. */}
            {confirmingPaidFatura && createPortal(
                <div
                    className="fixed inset-0 z-[150] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-4"
                    onMouseDown={e => { backdropMouseDownRef.current = e.target === e.currentTarget; }}
                    onClick={() => {
                        if (backdropMouseDownRef.current && !paymentLoading) { setConfirmingPaidFatura(null); setReceiptFile(null); setReceiptDate(hojeISO()); setReceiptValor(''); }
                        backdropMouseDownRef.current = false;
                    }}
                >
                    <div
                        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md max-h-full overflow-y-auto p-6 text-center animate-scale-in border dark:border-gray-700"
                        onClick={e => e.stopPropagation()}
                    >

                        <div className="w-16 h-16 bg-primary/10 text-primary rounded-full flex items-center justify-center mx-auto mb-4">
                            <CheckCircle2 size={32} />
                        </div>

                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1">Confirmar Pagamento</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                            Para registrar o pagamento da fatura de <span className="font-bold text-gray-800 dark:text-gray-200">{confirmingPaidFatura.mes_referencia}</span>, informe a data e o valor do pagamento e anexe o comprovante.
                        </p>

                        <div className="mb-5 text-left">
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                                Data do Pagamento *
                            </label>
                            <input
                                type="date"
                                value={receiptDate}
                                max={hojeISO()}
                                onChange={(e) => setReceiptDate(e.target.value)}
                                disabled={paymentLoading}
                                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-base sm:text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                            />
                            <p className="text-[11px] text-gray-400 mt-1.5">
                                A data que consta no comprovante — não a de hoje, se o cliente pagou antes.
                            </p>
                        </div>

                        <div className="mb-5 text-left">
                            <label htmlFor="valor-pago-historico" className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                                Valor Pago *
                            </label>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">R$</span>
                                <input
                                    id="valor-pago-historico"
                                    type="text"
                                    inputMode="decimal"
                                    autoComplete="off"
                                    placeholder="0,00"
                                    value={receiptValor}
                                    onChange={(e) => setReceiptValor(e.target.value)}
                                    disabled={paymentLoading}
                                    aria-invalid={receiptValor !== '' && receiptValorLido == null}
                                    className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-base sm:text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                                />
                            </div>
                            {receiptValor !== '' && receiptValorLido == null ? (
                                <p className="text-[11px] font-semibold text-red-500 mt-1.5">
                                    Valor inválido. Digite só o número, por exemplo 1.234,56.
                                </p>
                            ) : (
                                <p className="text-[11px] text-gray-400 mt-1.5">
                                    Já vem com o valor do boleto. Confira no comprovante e corrija se o cliente pagou outro valor.
                                </p>
                            )}
                        </div>

                        <div className="mb-6 text-left relative">
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                                Comprovante de Pagamento *
                            </label>

                            <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-xl cursor-pointer bg-gray-50 dark:bg-gray-750 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${receiptFile ? 'border-primary' : 'border-gray-300 dark:border-gray-600'}`}>
                                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                    <Upload className={`w-8 h-8 mb-2 ${receiptFile ? 'text-primary' : 'text-gray-400'}`} />
                                    {receiptFile ? (
                                        <p className="text-xs font-bold text-primary text-center px-4 truncate w-full">
                                            {receiptFile.name}
                                        </p>
                                    ) : (
                                        <p className="mb-1 text-xs text-gray-500 dark:text-gray-400 text-center px-4">
                                            <span className="font-bold text-primary">Clique para anexar</span> ou arraste o comprovante
                                        </p>
                                    )}
                                </div>
                                <input
                                    type="file"
                                    className="hidden"
                                    accept="image/*,.pdf"
                                    onChange={(e) => {
                                        if (e.target.files && e.target.files[0]) {
                                            setReceiptFile(e.target.files[0]);
                                        }
                                    }}
                                    disabled={paymentLoading}
                                />
                            </label>
                        </div>

                        <div className="flex gap-3 justify-center">
                            <button
                                onClick={() => {
                                    setConfirmingPaidFatura(null);
                                    setReceiptFile(null);
                                    setReceiptDate(hojeISO());
                                    setReceiptValor('');
                                }}
                                disabled={paymentLoading}
                                className="px-5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleConfirmPayment}
                                disabled={paymentLoading || !receiptFile || !receiptDate || receiptValorLido == null}
                                className="px-5 py-2.5 rounded-xl bg-primary text-white font-bold hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {paymentLoading ? 'Salvando...' : 'Confirmar'}
                            </button>
                        </div>

                    </div>
                </div>,
                document.body
            )}

        </div>
    );
};

export default HistoricoCobrancasTab;
